/**
 * Component for Multiple Rule Conditions settings
 *
 * @component
 */

import {
  Card,
  Col,
  DatePicker,
  Input,
  InputNumber,
  Row,
  Select,
  Typography,
} from 'antd';
import axios from 'axios';
import dayjs from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import React, { Component } from 'react';
import {
  frequentlyUsedCountries,
  nonFrequentlyUsedCountries,
} from '../../../../constants/constants';
import { SubscriptionContext } from '../../../../contexts/SubscriptionContext';

dayjs.extend(quarterOfYear);

const { Option } = Select;
const { Text } = Typography;
const { RangePicker } = DatePicker;

class RuleConditionSection extends Component {
  static contextType = SubscriptionContext;
  constructor(props) {
    super(props);
    this.state = {
      startDate: '',
      endDate: '',
      showDateInput: false,
      listCollection: [],
      returnReasons: [],
      returnReasonsLoaded: false,
    };
  }
  async componentDidMount() {
    let trigger, value;
    trigger = this.props.props?.selectedRule?.trigger;
    value = this.props.props?.selectedRule?.value;
    if (this.props.secondary) {
      trigger = this.props.props?.selectedRule?.secondaryTrigger;
      value = this.props.props?.selectedRule?.secondaryValue;
    }

    try {
      const response = await axios.get('/api/returnSettings/rules/listData');
      this.setState({
        listCollection: response.data.listCollections,
      });
    } catch (error) {
      console.error(error);
    }

    try {
      const returnReasonsResponse = await axios.get(
        '/api/returnSettings/returnReasons?isDashboard=true'
      );
      this.setState({
        returnReasons: returnReasonsResponse.data || [],
      });
    } catch (error) {
      console.error(error);
    } finally {
      this.setState({ returnReasonsLoaded: true });
    }

    if (trigger === 'orderDateRange') {
      const datesObject = JSON.parse(value);
      this.setState({
        startDate: dayjs(datesObject.startDate),
        endDate: dayjs(datesObject.endDate),
      });
    }
    this.setState({
      showDateInput: true,
    });
  }
  getCountriesList = () => {
    let countriesList = [];
    frequentlyUsedCountries.forEach((country) => {
      countriesList.push(
        <Option
          key={`frequentlyUsedCountries${country.code}`}
          value={country.code}
        >
          {country.name}
        </Option>
      );
    });

    const disabledoption = (
      <Option key="disabled" value="disabled" disabled={true}>
        ---------
      </Option>
    );
    countriesList.push(disabledoption);
    nonFrequentlyUsedCountries.forEach((country) => {
      countriesList.push(
        <Option
          key={`nonFrequentlyUsedCountries${country.code}`}
          value={country.code}
        >
          {country.name}
        </Option>
      );
    });

    return countriesList;
  };

  checkIfDisabled = (value) => {
    const { secondary, props } = this.props;
    const selectedRule = props?.selectedRule;
    let disabled = false;
    if (secondary && selectedRule?.trigger === value) {
      disabled =
        selectedRule?.secondaryConditionOperator === 'AND' ? true : false;
    }
    return disabled;
  };

  changeTrigger = (e) => {
    this.props.props.updateInput(
      e,
      this.props.secondary ? 'secondaryTrigger' : 'trigger'
    );
    const isOperatorTriggers = [
      'orderCountry',
      'orderTag',
      'selectedAction',
      'selectedReason',
      'discountCode',
      'paymentGateway',
      'productTag',
      'productSKU',
      'orderDateRange',
    ];
    const lessOperatorTriggers = [
      'orderDays',
      'originalOrderValue',
      'discountPercentage',
      'productDiscount',
    ];

    if (lessOperatorTriggers.includes(e)) {
      const value = e === 'originalOrderValue' ? 100.0 : 1;
      this.props.props.updateInput(
        value,
        this.props.secondary ? 'secondaryValue' : 'value'
      );
      this.props.props.updateInput(
        'less',
        this.props.secondary ? 'secondaryOperator' : 'operator'
      );
    }
    if (isOperatorTriggers.includes(e)) {
      this.props.props.updateInput(
        '',
        this.props.secondary ? 'secondaryValue' : 'value'
      );
      this.props.props.updateInput(
        'is',
        this.props.secondary ? 'secondaryOperator' : 'operator'
      );
    }
    if (e === 'productCollection') {
      this.props.props.updateInput(
        null,
        this.props.secondary ? 'secondaryValue' : 'value'
      );
      this.props.props.updateInput(
        'in',
        this.props.secondary ? 'secondaryOperator' : 'operator'
      );
    }
  };

  onChange = (dates, dateStrings) => {
    if (dateStrings[0] || dateStrings[1]) {
      this.setState(
        {
          startDate: dateStrings[0] && dayjs(dateStrings[0]),
          endDate: dateStrings[1] && dayjs(dateStrings[1]),
        },
        () => {
          const dateString = JSON.stringify({
            startDate: this.state.startDate,
            endDate: this.state.endDate,
          });
          this.props.props.updateInput(
            undefined,
            this.props.secondary ? 'secondaryValue' : 'value',
            dateString
          );
        }
      );
    } else {
      this.props.props.updateInput(
        undefined,
        this.props.secondary ? 'secondaryValue' : 'value'
      );
    }
  };

  getValueReturnReason = (value) => {
    if (value === undefined || value === null || value === '') return undefined;
    const parts = String(value)
      .split(',')
      .map((p) => p.trim());
    const allNumeric = parts.every((p) => /^\d+$/.test(p));
    if (!allNumeric) return undefined; // legacy free-text value - nothing to preselect
    return parts.map(Number);
  };

  getValueCountry = (countries) => {
    const countryNames = [
      ...frequentlyUsedCountries,
      ...nonFrequentlyUsedCountries,
    ].map((i) => i.code);

    if (typeof countries === 'number') {
      countries = countries.toString();
    }

    const countryArray =
      typeof countries === 'string' ? countries.split(',') : countries;

    return countryArray.filter((country) => countryNames.includes(country));
  };

  render() {
    let trigger, operator, value;
    trigger = this.props.props?.selectedRule?.trigger;
    operator = this.props.props?.selectedRule?.operator;
    value = this.props.props?.selectedRule?.value;

    if (this.props.secondary) {
      trigger = this.props.props?.selectedRule?.secondaryTrigger;
      operator = this.props.props?.selectedRule?.secondaryOperator;
      value = this.props.props?.selectedRule?.secondaryValue;
    }
    const { listCollection } = this.state;

    // Sort collections alphabetically by title
    const sortedCollections = [...listCollection].sort((a, b) => {
      if (!a.title || !b.title) return 0;
      return a.title.localeCompare(b.title);
    });

    const listCollectionOptions = sortedCollections.map((collection) => (
      <Option key={collection.id} value={collection.id}>
        {collection.title}
      </Option>
    ));
    return (
      <React.Fragment>
        <div className="flex-row u-rowGap--sm u-width--100">
          <div className="flex-col-xxs-12">
            <h4 data-intercom-target="trigger-header-configuration-advanced-rules">
              Trigger
            </h4>
            <Select
              defaultValue={
                this.props.props.selectedRule ? trigger : 'orderCountry'
              }
              style={{ width: '100%' }}
              placeholder="Select trigger"
              onChange={this.changeTrigger}
              suffixIcon={<span>Trigger</span>}
              data-intercom-target="trigger-select-configuration-advanced-rules"
            >
              <Option key="disabled" value="disabled" disabled={true}>
                --- Basic rules based on order information ---
              </Option>
              <Option
                key="orderCountry"
                value="orderCountry"
                disabled={this.checkIfDisabled('orderCountry')}
              >
                Order Country
              </Option>
              <Option
                key="orderTag"
                value="orderTag"
                disabled={this.checkIfDisabled('orderTag')}
              >
                Order Tag
              </Option>
              <Option
                key="orderDays"
                value="orderDays"
                disabled={this.checkIfDisabled('orderDays')}
              >
                Days since delivery date
              </Option>
              <Option
                key="orderDateRange"
                value="orderDateRange"
                disabled={this.checkIfDisabled('orderDateRange')}
              >
                Order Date Range
              </Option>
              <Option
                key="discountCode"
                value="discountCode"
                disabled={this.checkIfDisabled('discountCode')}
              >
                Discount code
              </Option>
              <Option
                key="discountPercentage"
                value="discountPercentage"
                disabled={this.checkIfDisabled('discountPercentage')}
              >
                Discount in Percent of Order Total
              </Option>
              <Option
                key="originalOrderValue"
                value="originalOrderValue"
                disabled={this.checkIfDisabled('originalOrderValue')}
              >
                {' '}
                Order Total
              </Option>
              <Option
                key="paymentGateway"
                value="paymentGateway"
                disabled={this.checkIfDisabled('paymentGateway')}
              >
                Payment Gateway
              </Option>
              <Option key="disabled2" value="disabled2" disabled={true}>
                --- Rules based on customer input ---
              </Option>
              <Option
                key="selectedAction"
                value="selectedAction"
                disabled={this.checkIfDisabled('selectedAction')}
              >
                Selected return action (e.g. Refund, Exchange, etc)
              </Option>
              <Option
                key="selectedReason"
                value="selectedReason"
                disabled={this.checkIfDisabled('selectedReason')}
              >
                Selected return reason
              </Option>
              <Option key="disabled3" value="disabled3" disabled={true}>
                --- Rules based on Products ---
              </Option>
              <Option
                key="productTag"
                value="productTag"
                disabled={this.checkIfDisabled('productTag')}
              >
                Product Tag
              </Option>
              <Option
                key="productSKU"
                value="productSKU"
                disabled={this.checkIfDisabled('productSKU')}
              >
                Product SKU
              </Option>
              <Option
                key="productDiscount"
                value="productDiscount"
                disabled={this.checkIfDisabled('productDiscount')}
              >
                Discount in Percent of Product Price
              </Option>
              <Option
                key="productCollection"
                value="productCollection"
                disabled={this.checkIfDisabled('productCollection')}
              >
                Product Collections
              </Option>
            </Select>
          </div>
          <div className="flex-col-xxs-12">
            <h4 data-intercom-target="operator-header-configuration-advanced-rules">
              Operator
            </h4>
            {[
              'orderCountry',
              'orderTag',
              'selectedAction',
              'selectedReason',
              'discountCode',
              'paymentGateway',
              'productTag',
              'productSKU',
            ].includes(trigger) ? (
              <Select
                defaultValue={
                  this.props.props.selectedRule &&
                  ['is', 'isNot'].includes(operator)
                    ? operator
                    : 'is'
                }
                style={{ width: '100%' }}
                placeholder="Select operator"
                onChange={(e) =>
                  this.props.props.updateInput(
                    e,
                    this.props.secondary ? 'secondaryOperator' : 'operator'
                  )
                }
                suffixIcon={<span>Operator</span>}
                data-intercom-target="operator-select-configuration-advanced-rules"
              >
                <Option key="is" value="is">
                  is
                </Option>
                <Option key="isNot" value="isNot">
                  is not
                </Option>
              </Select>
            ) : null}
            {['productCollection'].includes(trigger) ? (
              <Select
                defaultValue={
                  this.props.props.selectedRule &&
                  ['in', 'notIn'].includes(operator)
                    ? operator
                    : 'in'
                }
                style={{ width: '100%' }}
                placeholder="Select operator"
                onChange={(e) =>
                  this.props.props.updateInput(
                    e,
                    this.props.secondary ? 'secondaryOperator' : 'operator'
                  )
                }
                suffixIcon={<span>Operator</span>}
                data-intercom-target="operator-select-product-collection-configuration-advanced-rules"
              >
                <Option key={1} value="in">
                  In
                </Option>
                <Option key={2} value="notIn">
                  Not in
                </Option>
              </Select>
            ) : null}
            {['orderDateRange'].includes(trigger) && (
              <Select
                defaultValue={
                  this.props.props.selectedRule &&
                  ['is', 'isNot'].includes(operator)
                    ? operator
                    : undefined
                }
                style={{ width: '100%' }}
                placeholder="Select operator"
                onChange={(e) =>
                  this.props.props.updateInput(
                    e,
                    this.props.secondary ? 'secondaryOperator' : 'operator'
                  )
                }
                suffixIcon={<span>Operator</span>}
                data-intercom-target="operator-select-order-date-range-configuration-advanced-rules"
              >
                <Option key={1} value="is">
                  is
                </Option>
                <Option key={2} value="isNot">
                  is not
                </Option>
              </Select>
            )}
            {[
              'orderDays',
              'originalOrderValue',
              'discountPercentage',
              'productDiscount',
            ].includes(trigger) ? (
              <Select
                defaultValue={
                  this.props.props.selectedRule &&
                  ['less', 'more'].includes(operator)
                    ? operator
                    : 'less'
                }
                style={{ width: '100%' }}
                placeholder="Select operator"
                onChange={(e) =>
                  this.props.props.updateInput(
                    e,
                    this.props.secondary ? 'secondaryOperator' : 'operator'
                  )
                }
                suffixIcon={<span>Operator</span>}
              >
                <Option key={1} value="less">
                  less than
                </Option>
                <Option key={2} value="more">
                  more than
                </Option>
              </Select>
            ) : null}
          </div>
          <div className="flex-col-xxs-12">
            <h4 data-intercom-target="value-header-configuration-advanced-rules">
              Value
            </h4>
            <Row
              type="flex"
              justify="center"
              align="top"
              style={{ marginBottom: 20, textAlign: 'center', width: '100%' }}
            >
              <Col span={24}>
                {['orderDateRange'].includes(trigger) ? (
                  <>
                    {(!this.props.props?.selectedRule?.name ||
                      (this.props.props?.selectedRule?.name &&
                        this.state.showDateInput)) && (
                      <RangePicker
                        style={{ width: '100%', marginTop: 10 }}
                        defaultValue={[
                          this.state.startDate,
                          this.state.endDate,
                        ]}
                        presets={[
                          {
                            label: 'Today',
                            value: [dayjs(), dayjs()],
                          },
                          {
                            label: 'Yesterday',
                            value: [
                              dayjs().subtract(1, 'day').startOf('day'),
                              dayjs().subtract(1, 'day').endOf('day'),
                            ],
                          },
                          {
                            label: 'Last Week',
                            value: [
                              dayjs().subtract(1, 'week').startOf('week'),
                              dayjs().subtract(1, 'week').endOf('week'),
                            ],
                          },
                          {
                            label: 'This Month',
                            value: [
                              dayjs().startOf('month'),
                              dayjs().endOf('month'),
                            ],
                          },
                          {
                            label: 'Last Month',
                            value: [
                              dayjs().subtract(1, 'month').startOf('month'),
                              dayjs().subtract(1, 'month').endOf('month'),
                            ],
                          },
                          {
                            label: 'Last Quarter',
                            value: [
                              dayjs().subtract(1, 'quarter').startOf('quarter'),
                              dayjs().subtract(1, 'quarter').endOf('quarter'),
                            ],
                          },
                        ]}
                        onChange={this.onChange}
                        onCalendarChange={(dateArray) => {
                          const endDateSelected = dateArray[1] != null;
                          if (endDateSelected) {
                            return this.setState({
                              date: undefined,
                            });
                          }

                          const startDateSelected = dateArray[0];
                          if (startDateSelected) {
                            this.setState({
                              date: dateArray[0],
                            });
                          }
                        }}
                        data-intercom-target="value-rangepicker-configuration-advanced-rules"
                      />
                    )}
                  </>
                ) : null}
                {[
                  'orderTag',
                  'discountCode',
                  'paymentGateway',
                  'productTag',
                  'productSKU',
                ].includes(trigger) ? (
                  <Input
                    defaultValue={
                      this.props.props.selectedRule ? value : undefined
                    }
                    style={{ width: '100%' }}
                    onChange={(e) =>
                      this.props.props.updateInput(
                        e,
                        this.props.secondary ? 'secondaryValue' : 'value'
                      )
                    }
                    data-intercom-target="value-input-configuration-advanced-rules"
                  />
                ) : null}

                {[
                  'orderDays',
                  'discountPercentage',
                  'productDiscount',
                ].includes(trigger) ? (
                  <InputNumber
                    defaultValue={
                      this.props.props.selectedRule && value && !isNaN(value)
                        ? value
                        : 1
                    }
                    style={{ width: '100%' }}
                    min={1}
                    precision={0}
                    onChange={(e) =>
                      this.props.props.updateInput(
                        e,
                        this.props.secondary ? 'secondaryValue' : 'value'
                      )
                    }
                    data-intercom-target="value-inputnumber-configuration-advanced-rules"
                  />
                ) : null}

                {['originalOrderValue'].includes(trigger) ? (
                  <InputNumber
                    defaultValue={
                      this.props.props.selectedRule && value ? value : 100.0
                    }
                    style={{ width: '100%' }}
                    min={0}
                    precision={2}
                    onChange={(e) =>
                      this.props.props.updateInput(
                        e,
                        this.props.secondary ? 'secondaryValue' : 'value'
                      )
                    }
                    data-intercom-target="value-inputnumber-original-configuration-advanced-rules"
                  />
                ) : null}
                {trigger === 'productCollection' ? (
                  <Select
                    defaultValue={
                      this.props.props.selectedRule && value
                        ? parseInt(value)
                        : undefined
                    }
                    placeholder="Choose collection"
                    onChange={(e) =>
                      this.props.props.updateInput(
                        e,
                        this.props.secondary ? 'secondaryValue' : 'value'
                      )
                    }
                    style={{ width: '100%' }}
                    data-intercom-target="value-select-product-collection-configuration-advanced-rules"
                  >
                    {listCollectionOptions}
                  </Select>
                ) : null}
                {trigger === 'selectedReason' ? (
                  <>
                    {!this.state.returnReasonsLoaded ? (
                      <Select
                        key="return-reasons-loading"
                        mode="multiple"
                        loading
                        disabled
                        placeholder="Loading return reasons..."
                        style={{ width: '100%' }}
                        data-intercom-target="value-select-return-reason-configuration-advanced-rules"
                      />
                    ) : (
                      <Select
                        key="return-reasons-loaded"
                        defaultValue={
                          this.props.props.selectedRule && value
                            ? this.getValueReturnReason(value)
                            : undefined
                        }
                        mode="multiple"
                        placeholder="Select return reason"
                        onChange={(e) =>
                          this.props.props.updateInput(
                            e,
                            this.props.secondary ? 'secondaryValue' : 'value'
                          )
                        }
                        showSearch
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                          option.props.children
                            .toLowerCase()
                            .indexOf(input.toLowerCase()) >= 0
                        }
                        style={{ width: '100%' }}
                        data-intercom-target="value-select-return-reason-configuration-advanced-rules"
                      >
                        {this.state.returnReasons.map((reason) => (
                          <Option key={reason.id} value={reason.id}>
                            {reason.reason}
                          </Option>
                        ))}
                        {(this.getValueReturnReason(value) || [])
                          .filter(
                            (id) =>
                              !this.state.returnReasons.some(
                                (reason) => reason.id === id
                              )
                          )
                          .map((danglingId) => (
                            <Option key={danglingId} value={danglingId}>
                              ⚠️ Return reason no longer exists
                            </Option>
                          ))}
                      </Select>
                    )}
                    {this.state.returnReasonsLoaded &&
                    this.props.props.selectedRule &&
                    value &&
                    this.getValueReturnReason(value) === undefined ? (
                      <Text
                        type="warning"
                        style={{ display: 'block', marginTop: 4 }}
                      >
                        ⚠️ This rule currently matches the free-text value "
                        {value}" (set before the dropdown existed). Select from
                        the dropdown above to replace it, or leave as-is to keep
                        matching this exact text.
                      </Text>
                    ) : null}
                  </>
                ) : null}
                {trigger === 'selectedAction' ? (
                  <Select
                    defaultValue={
                      this.props.props.selectedRule &&
                      [
                        'refundToOriginal',
                        'refundToCredit',
                        'refundToExchange',
                        'advancedExchange',
                        'storeWideExchange',
                        'refundToCustom1',
                        'refundToCustom2',
                        'refundToCustom3',
                      ].includes(value)
                        ? value
                        : undefined
                    }
                    placeholder="Select Action"
                    onChange={(e) =>
                      this.props.props.updateInput(
                        e,
                        this.props.secondary ? 'secondaryValue' : 'value'
                      )
                    }
                    style={{ width: '100%' }}
                    data-intercom-target="value-select-action-configuration-advanced-rules"
                  >
                    <Option key="refundToOriginal" value="refundToOriginal">
                      Refund
                    </Option>
                    <Option key="refundToCredit" value="refundToCredit">
                      Store Credit
                    </Option>
                    <Option key="refundToExchange" value="refundToExchange">
                      Exchange
                    </Option>
                    {this.context.featureAdvancedExchanges ? (
                      <Option key="advancedExchange" value="advancedExchange">
                        Collection Exchange
                      </Option>
                    ) : null}
                    {this.context.featureStoreWideExchange ? (
                      <Option key="storeWideExchange" value="storeWideExchange">
                        Store-Wide Exchange
                      </Option>
                    ) : null}
                    <Option key="refundToCustom1" value="refundToCustom1">
                      Custom Option 1
                    </Option>
                    <Option key="refundToCustom2" value="refundToCustom2">
                      Custom Option 2
                    </Option>
                    <Option key="refundToCustom3" value="refundToCustom3">
                      Custom Option 3
                    </Option>
                  </Select>
                ) : null}
                {this.props.props.selectedRule && trigger === 'orderCountry' ? (
                  <Select
                    defaultValue={
                      this.props.props.selectedRule && value
                        ? this.getValueCountry(value)
                        : undefined
                    }
                    mode="multiple"
                    placeholder="Select country"
                    onChange={(e) =>
                      this.props.props.updateInput(
                        e,
                        this.props.secondary ? 'secondaryValue' : 'value'
                      )
                    }
                    showSearch
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      option.props.children
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                    style={{ width: '100%' }}
                    suffixIcon={<span>Country</span>}
                    data-intercom-target="value-select-country-configuration-advanced-rules"
                  >
                    {this.getCountriesList()}
                  </Select>
                ) : null}
              </Col>
            </Row>

            {this.props.props.selectedRule &&
            ['paymentGateway'].includes(trigger) ? (
              <Row
                type="flex"
                justify="space-around"
                align="top"
                style={{ marginTop: 20, width: '100%' }}
              >
                <Col xs={24} sm={24} md={24} lg={24}>
                  <Card
                    size="small"
                    title="Rule info"
                    data-intercom-target="rule-info-card-payment-gateway-configuration-advanced-rules"
                  >
                    <p>
                      <Text>
                        <strong>Important:</strong> You need to put the
                        "technical value" in this field, e.g. for Shopify
                        Payments it is shopify_payments.{' '}
                        <a
                          href="https://support.richreturns.io/en/articles/17693618-list-of-payment-gateway-names?utm_source=merchant_panel"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Click here for a list
                        </a>{' '}
                        of commonly used payment gateways and their technical
                        values.
                      </Text>
                    </p>
                  </Card>
                </Col>
              </Row>
            ) : null}

            {this.props.props.selectedRule &&
            ['originalOrderValue'].includes(trigger) ? (
              <Row
                type="flex"
                justify="space-around"
                align="top"
                style={{ marginTop: 20, width: '100%' }}
              >
                <Col xs={24} sm={24} md={24} lg={24}>
                  <Card
                    size="small"
                    title="Rule info"
                    data-intercom-target="rule-info-card-original-order-value-configuration-advanced-rules"
                  >
                    <p>
                      <Text>
                        <strong>Important:</strong> Value must be an amount in
                        your Store's default currency, e.g. USD
                      </Text>
                    </p>
                  </Card>
                </Col>
              </Row>
            ) : null}

            {this.props.props.selectedRule &&
            ['discountPercentage', 'productDiscount'].includes(trigger) ? (
              <Row
                type="flex"
                justify="space-around"
                align="top"
                style={{ marginTop: 20, width: '100%' }}
              >
                <Col xs={24} sm={24} md={24} lg={24}>
                  <Card
                    size="small"
                    title="Rule info"
                    data-intercom-target="rule-info-card-discount-percentage-configuration-advanced-rules"
                  >
                    <p>
                      <Text>
                        <strong>Note:</strong> Value in percent (e.g. 21) of
                        total{' '}
                        {trigger === 'productDiscount'
                          ? 'product value'
                          : 'order value of the original order'}
                        . If you use "less than" be aware that this also
                        includes{' '}
                        {trigger === 'productDiscount'
                          ? 'products '
                          : 'orders '}
                        without a discount at all.
                      </Text>
                    </p>
                  </Card>
                </Col>
              </Row>
            ) : null}

            {this.props.props.selectedRule &&
            ['selectedAction', 'selectedReason'].includes(trigger) ? (
              <Row
                type="flex"
                justify="space-around"
                align="top"
                style={{ marginTop: 20, width: '100%' }}
              >
                <Col xs={24} sm={24} md={24} lg={24}>
                  <Card
                    size="small"
                    title="Rule info"
                    data-intercom-target="rule-info-card-selected-action-configuration-advanced-rules"
                  >
                    <p>
                      <Text>
                        <strong>1.</strong> This rule is applied as soon as the
                        condition is fulfilled for at least one product in the
                        return.
                      </Text>
                    </p>
                    <p>
                      <Text>
                        <strong>2.</strong> This rule is applied in response to
                        customer input. Therefore it will overwrite other basic
                        rules on the properties you set. It will not overwrite
                        other rules of the same type.
                      </Text>
                    </p>
                  </Card>
                </Col>
              </Row>
            ) : null}
          </div>
        </div>
      </React.Fragment>
    );
  }
}
export default RuleConditionSection;
